import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { MaintenanceTypesQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { MaintenanceTypeFilters } from '@/types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { EditDialog } from './edit_dialog'
import { NewDialog } from './new_dialog'
import { Badge } from './badge'

interface Props extends TableProps {
  createable?: boolean
}

export const Table = ({ pageSize = 20, onIndex = false, createable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<MaintenanceTypesQuery>(
    Query,
    { variables: { first: pageSize } }
  )
  const {
    pageInfo,
    setOpenNew,
    openShow,
    openNew,
    showShow,
    hideShow,
    resourceId,
  } = useTable({ pageInfo: data?.maintenanceTypes.pageInfo, onIndex, basePath: '/settings/maintenance-types' })

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<MaintenanceTypeFilters>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        headings={[{ name: 'Name' }, { name: 'Tasks' }]}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.maintenanceTypes.nodes = dedupe([
              ...previousData.maintenanceTypes.nodes,
              ...fetchMoreResult.maintenanceTypes.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, nameCont: value })),
            type: 'text',
            placeholder: 'Now, what did we call it?'
          },
        ]}
        rows={data.maintenanceTypes.nodes.map(mt => (
          {
            id: mt.id,
            columns: [
              <Badge maintenanceType={mt} />,
              mt.maintenanceTypeTasks.length
            ]
          }
        ))}
        onRowClick={showShow}
      />
      { resourceId && <EditDialog id={resourceId} open={openShow} setOpen={hideShow} /> }
      <NewDialog open={openNew} setOpen={setOpenNew} />
    </>
  )
}
